import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Lock, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { login } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This invite link is missing its token.');
      setIsLoading(false);
      return;
    }
    axios.get(`${API}/signatures/team/invite/${token}`)
      .then((res) => setInvite(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'This invite link is invalid or has already been used.'))
      .finally(() => setIsLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    if (password.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/signatures/team/invite/${token}/accept`, { password });
      const result = await login(invite.email, password);
      if (result.success) {
        toast({ title: 'Welcome!', description: 'Your account is ready.' });
        navigate('/account/signatures/new');
      } else {
        toast({ title: 'Account created', description: 'Please sign in with your new password.' });
        navigate('/login');
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to accept this invite',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      <section className="pt-32 pb-24">
        <div className="max-w-md mx-auto px-4">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#FF6B00]" />
            </div>
          ) : error ? (
            <div className="bg-[#302f2c] border border-[#4a3520] rounded-2xl p-8 text-center">
              <h1 className="text-white font-bold text-xl mb-2">Invite not available</h1>
              <p className="text-[#888680] text-sm mb-6">{error}</p>
              <Link to="/login" className="text-[#FF6B00] hover:underline text-sm font-semibold">
                Go to sign in
              </Link>
            </div>
          ) : (
            <div className="bg-[#302f2c] border border-[#4a3520] rounded-2xl p-8">
              <h1 className="text-white font-bold text-2xl mb-1">You're invited</h1>
              <p className="text-[#888680] text-sm mb-6">
                {invite.owner_name} added <span className="text-white">{invite.email}</span> to their
                Email Signature Builder team. Set a password to finish setting up your account.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-[#dfddd6] mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888680]" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full pl-11 pr-4 py-3 bg-[#1a1c1b] border border-[#4a3520] rounded-xl text-white placeholder-[#666] focus:outline-none focus:border-[#FF6B00] transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#dfddd6] mb-2">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888680]" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full pl-11 pr-4 py-3 bg-[#1a1c1b] border border-[#4a3520] rounded-xl text-white placeholder-[#666] focus:outline-none focus:border-[#FF6B00] transition-colors"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#FF6B00] hover:bg-[#e05f00] text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Set Password & Continue <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default AcceptInvitePage;
